// Renderer, Licht, Label-Renderer und Render-Schleife.
import * as THREE from "three"
import { CSS2DRenderer } from "three/addons/renderers/CSS2DRenderer.js"
import gsap from "gsap"
import { COLORS } from "./world/palette.js"

export class Stage {
  constructor({ canvas, labels, quality = "high" }) {
    this.quality = quality
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: "high-performance" })
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, quality === "low" ? 1 : 1.5))
    this.renderer.shadowMap.enabled = quality !== "low"
    this.renderer.shadowMap.type = THREE.PCFShadowMap
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping
    this.renderer.toneMappingExposure = 1.05

    this.labelRenderer = new CSS2DRenderer({ element: labels })

    this.scene = new THREE.Scene()
    this.scene.background = new THREE.Color(COLORS.navy)
    this.scene.fog = new THREE.Fog(COLORS.navy, 240, 470)

    this.camera = new THREE.PerspectiveCamera(32, 1, 1, 600)

    const hemi = new THREE.HemisphereLight(0xdfe8ff, 0x3a4a2a, 1.4)
    this.scene.add(hemi)
    const sun = new THREE.DirectionalLight(0xfff4e0, 2.6)
    sun.position.set(-40, 70, 35)
    sun.castShadow = quality !== "low"
    sun.shadow.mapSize.set(2048, 2048)
    sun.shadow.camera.left = -75
    sun.shadow.camera.right = 75
    sun.shadow.camera.top = 75
    sun.shadow.camera.bottom = -75
    sun.shadow.camera.far = 200
    sun.shadow.bias = -0.0006
    sun.shadow.normalBias = 0.04
    this.scene.add(sun)
    this.sun = sun

    // Horizontale Bildverschiebung, damit die Szene neben seitlichen Panels zentriert bleibt.
    this.shift = { x: 0 }

    this.timer = new THREE.Timer()
    this.updaters = []
    this.resize()
    window.addEventListener("resize", () => this.resize())
  }

  resize() {
    const width = window.innerWidth
    const height = window.innerHeight
    this.renderer.setSize(width, height, false)
    this.labelRenderer.setSize(width, height)
    this.camera.aspect = width / height
    this.applyShift()
  }

  applyShift() {
    const width = window.innerWidth
    const height = window.innerHeight
    if (Math.abs(this.shift.x) < 0.5) this.camera.clearViewOffset()
    else this.camera.setViewOffset(width, height, -this.shift.x, 0, width, height)
    this.camera.updateProjectionMatrix()
  }

  // px > 0 schiebt die Szene nach rechts, px < 0 nach links.
  setShift(px, duration = 1.2) {
    gsap.to(this.shift, { x: px, duration, ease: "power2.inOut", onUpdate: () => this.applyShift() })
  }

  onUpdate(fn) {
    this.updaters.push(fn)
  }

  start() {
    this.renderer.setAnimationLoop((timestamp) => {
      this.timer.update(timestamp)
      // Im ersten Frame kann der Timer leicht negativ sein (Frame-Zeitstempel vor Erzeugung)
      const dt = THREE.MathUtils.clamp(this.timer.getDelta(), 0, 0.05)
      const time = Math.max(0, this.timer.getElapsed())
      for (const fn of this.updaters) fn(dt, time)
      this.renderer.render(this.scene, this.camera)
      this.labelRenderer.render(this.scene, this.camera)
    })
  }
}
